import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@criteria-labs/react'
import { Resource } from '@criteria-labs/docs-kit'
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { DeleteResource } from '../changes/resource'
import { EndpointLine } from '../endpoint/EndpointLine'
import { useRouter } from '../router'
import { useDispatchChange } from '../useDispatchChange'

export function ResourceList({ resources }: { resources: Resource[] }) {
  const dispatchChange = useDispatchChange()
  const [deletingResource, setDeletingResource] = useState<Resource | null>(null)
  const { push } = useRouter()

  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const sortedResourceEntries = resources
    .map((resource, index) => [resource, index] as [Resource, number])
    .sort((a, b) => {
      const ascendingSort = a[0].tag.name < b[0].tag.name ? -1 : a[0].tag.name > b[0].tag.name ? 1 : 0
      return sortDirection === 'asc' ? ascendingSort : -1 * ascendingSort
    })

  const SortIcon = sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon

  return (
    <>
      <Table dense bleed className="[--gutter:0]">
        <TableHead>
          <TableRow>
            <TableHeader>
              <button
                className="group flex w-full items-center gap-1 dark:hover:text-white"
                onClick={() => {
                  setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                }}
              >
                Name <SortIcon className="size-4" />
              </button>
            </TableHeader>
            <TableHeader>Endpoints</TableHeader>
            <TableHeader className="relative w-0">
              <span className="sr-only">Actions</span>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedResourceEntries.map(([resource, resourceIndex]) => (
            <TableRow
              key={resource.slug}
              href="#"
              onClick={() => {
                push(`/resources/${resourceIndex}`)
              }}
            >
              <TableCell>{resource.summary}</TableCell>
              <TableCell>
                {resource.endpoints.length === 1 ? '1 endpoint' : `${resource.endpoints.length} endpoints`}
              </TableCell>
              <TableCell>
                <Button plain aria-label="Delete endpoint" onClick={() => setDeletingResource(resource)}>
                  <XMarkIcon />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Alert open={deletingResource !== null} onClose={() => setDeletingResource(null)}>
        <AlertTitle>Delete Resource?</AlertTitle>
        {deletingResource && (
          <AlertDescription>
            <p>The resource {deletingResource.summary} will be deleted, along with the following endpoints:</p>
            <ul>
              {deletingResource.endpoints.map((endpoint) => (
                <li key={endpoint.slug}>
                  <EndpointLine endpoint={endpoint} />
                </li>
              ))}
            </ul>
          </AlertDescription>
        )}
        <AlertActions>
          <Button plain onClick={() => setDeletingResource(null)}>
            Cancel
          </Button>
          {deletingResource && (
            <Button
              onClick={() => {
                dispatchChange(new DeleteResource(deletingResource?.tag.name, true))
                setDeletingResource(null)
              }}
            >
              Delete
            </Button>
          )}
        </AlertActions>
      </Alert>
    </>
  )
}
